/**
 * RenewalsTracker (C-1e) — marketing-authorisation renewal deadline wall.
 * Registered products whose registration (cert issue + 5y) needs renewal,
 * bucketed Overdue / ≤90d / ≤180d / ≤12mo / beyond. Click a row → the
 * product's register detail. Route: /regulatory/renewals
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from './Regulatory.module.css';
import { useRenewalsQuery, RENEWAL_BUCKET_LABEL, CATEGORY_LABEL } from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function bucketClass(b: string) {
  if (b === 'OVERDUE') return styles.stageWarn;
  if (b === 'DUE_90' || b === 'DUE_180') return styles.stageActive;
  return styles.stageDone;
}
function dueLabel(days: number | null) {
  if (days == null) return '—';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `in ${days}d`;
}

export function RenewalsTracker() {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState('');
  const q = useRenewalsQuery(bucket || undefined);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Regulatory Affairs · RA-PLM"
        title="Renewals Tracker"
        subtitle="Marketing-authorisation renewals across the registered portfolio. Renewal due = certificate issue date + 5 years; work the wall overdue-first."
      />

      {stats && (
        <div className={styles.kpiRow}>
          <button type="button" className={`${styles.kpi} ${styles.kpiLink} ${stats.overdue > 0 ? styles.kpiAlert : ''}`} onClick={() => setBucket('OVERDUE')}>
            <span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span>
          </button>
          <button type="button" className={`${styles.kpi} ${styles.kpiLink} ${stats.due_90 > 0 ? styles.kpiWarn : ''}`} onClick={() => setBucket('DUE_90')}>
            <span className={styles.kpiNum}>{stats.due_90}</span><span className={styles.kpiLabel}>Due ≤ 90 days</span>
          </button>
          <button type="button" className={`${styles.kpi} ${styles.kpiLink}`} onClick={() => setBucket('DUE_180')}>
            <span className={styles.kpiNum}>{stats.due_180}</span><span className={styles.kpiLabel}>Due ≤ 180 days</span>
          </button>
          <button type="button" className={`${styles.kpi} ${styles.kpiLink}`} onClick={() => setBucket('DUE_365')}>
            <span className={styles.kpiNum}>{stats.due_365}</span><span className={styles.kpiLabel}>Due ≤ 12 months</span>
          </button>
          <button type="button" className={`${styles.kpi} ${styles.kpiLink}`} onClick={() => setBucket('')}>
            <span className={styles.kpiNum}>{stats.total}</span><span className={styles.kpiLabel}>All renewals</span>
          </button>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Renewal window filter" value={bucket} onChange={(e) => setBucket(e.target.value)} className={styles.select}>
          <option value="">All windows</option>
          {Object.keys(RENEWAL_BUCKET_LABEL).map((b) => <option key={b} value={b}>{RENEWAL_BUCKET_LABEL[b]}</option>)}
        </select>
        <span className={styles.resultCount}>{items.length} product{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading renewals…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No registered products fall in this renewal window.</div>
      ) : (
        <table className={styles.table}>
          <thead><tr><th>Product</th><th>Reg. number</th><th>Category</th><th>Certificate issued</th><th>Renewal due</th><th>Window</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.productId} className={styles.row} onClick={() => navigate({ to: '/regulatory/products/$id', params: { id: p.productId } })}>
                <td>{p.brandName || p.molecule || p.productCode}<span className={styles.sub}> · {p.productCode}</span></td>
                <td className={styles.mono}>{p.registrationNumber || '—'}</td>
                <td>{CATEGORY_LABEL[p.category] ?? p.category}</td>
                <td>{fmtDate(p.certificateIssueDate)}</td>
                <td>{fmtDate(p.renewalDueDate)}<span className={styles.sub}> · {dueLabel(p.daysToDue)}</span></td>
                <td><span className={`${styles.pill} ${bucketClass(p.bucket)}`}>{RENEWAL_BUCKET_LABEL[p.bucket] ?? p.bucket}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
